
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { analyzeWebsiteWithAI } from "@/utils/websiteAnalyzer";
import { BusinessData } from "@/api/business/types";

interface UseWebsiteAnalyzerProps {
  businessData: BusinessData;
  updateBusinessData: (key: string, value: string | string[] | Record<string, string> | number) => void;
  updateMultipleFields: (updates: Record<string, string | string[] | number | Record<string, string>>) => void;
}

export const useWebsiteAnalyzer = ({ 
  businessData, 
  updateBusinessData,
  updateMultipleFields
}: UseWebsiteAnalyzerProps) => {
  const { toast } = useToast();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);

  const analyzeWebsite = async () => {
    // Check if analysis was done in the last 24 hours (86400000 ms)
    const now = Date.now();
    const timeSinceLastAnalysis = now - (businessData.lastAnalyzedAt || 0);
    const analysisCooldown = 86400000; // 24 hours in milliseconds

    if (businessData.lastAnalyzedAt && timeSinceLastAnalysis < analysisCooldown) {
      const hoursRemaining = Math.ceil((analysisCooldown - timeSinceLastAnalysis) / 3600000);
      toast({
        title: "Analysis limit reached",
        description: `You can analyze your website again in ${hoursRemaining} hour${hoursRemaining > 1 ? 's' : ''}.`,
        variant: "default",
      });
      return;
    }

    if (!businessData.website) {
      toast({
        title: "Website URL required",
        description: "Please enter your business website URL to analyze.",
        variant: "destructive",
      });
      return;
    }

    setIsAnalyzing(true);
    setAnalysisError(null);
    setAttempts(prev => prev + 1);
    
    try {
      // Add https:// if not present
      let url = businessData.website;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
        updateBusinessData('website', url);
      }
      
      console.log(`Analyzing website: ${url} (attempt ${attempts + 1})`);
      const data = await analyzeWebsiteWithAI(url);
      
      // Create an updates object for all the changes
      const updates: Record<string, string | string[] | number | Record<string, string>> = {
        lastAnalyzedAt: now
      };
      
      // Add discovered data to the updates
      if (data.keywords && data.keywords.length > 0) {
        updates.keywords = data.keywords;
        updates.businessTags = data.keywords;
      }
      
      if (data.colors && data.colors.length > 0) {
        updates.colors = data.colors;
      }
      
      // Suggest description if empty
      if (!businessData.description && data.description) {
        updates.description = data.description;
      }
      
      // Suggest brand voice if empty
      if (!businessData.brandVoice) {
        let voiceSuggestion = `${data.tone || 'professional'} and ${data.formality || 'formal'}`;
        updates.brandVoice = voiceSuggestion;
      }
      
      // Update social media profiles if found
      if (data.socialMedia && Object.keys(data.socialMedia).length > 0) {
        const existingSocialMedia = businessData.socialMedia || {};
        updates.socialMedia = { ...existingSocialMedia, ...data.socialMedia };
      }
      
      // Apply all updates at once
      updateMultipleFields(updates);
      
      toast({
        title: "Website analysis complete",
        description: "We've gathered insights from your website to personalize your experience.",
      });
    } catch (error: any) {
      console.error("Website analysis error:", error);
      setAnalysisError(error.message || "Failed to analyze website. Please check the URL and try again.");
      toast({
        title: "Analysis failed",
        description: error.message || "We couldn't analyze your website. Please check the URL and try again.",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  return {
    analyzeWebsite,
    isAnalyzing,
    analysisError
  };
};
